import React from 'react'
import _ from 'es-toolkit/compat'
import i18n from 'i18next'
import { useParams } from 'react-router-dom'

import { ItemFetchError, useApi } from '@core/api'
import type {
  FolderPermission,
  GetDraftFolderData,
  ListFoldersOutput,
  MoveFolderItemsInput,
  PreValidateExportInput,
  RemoveFolderItemsInput,
  UpdateFolderPermissionsInput,
} from '@core/api/generated'
import { addSnack } from '@core/snack/snack-state'
import { Filters, FilterType, useMyFolders } from '@pages/my-folders/my-folders-state'
import { FolderWithParent, getFlatFolderList } from '@pages/my-folders/my-folders-utils'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from '@tanstack/react-query'

declare global {
  interface Window {
    __DRAFT_ITEMS_LIMIT__?: number
  }
}

export const useFolderParams = () => {
  const params = useParams<{ folderId?: string; itemId?: string }>()

  return {
    folderId: params.folderId,
    itemId: params.itemId,
  }
}

export const queryKeys = {
  all: ['folders'],
  list: ['folders', 'list', 'full'],
  listSearch: (filters: Filters) => ['folders', 'list', 'search', JSON.stringify(filters)],
  listBasic: ['folders', 'list', 'basic'],
  flatList: ['folders', 'list', 'flatList'],
  flatListWithStats: ['folders', 'list', 'flatListWithStats'],
  permissions: (folderId: string) => ['folders', 'permissions', folderId],
  details: (folderId: string) => ['folders', 'details', folderId],
  item: (identifier: string) => ['folders', 'item', identifier],
  preValidateExport: (key: string) => ['folders', 'preValidateExport', key],
  drafts: (filters: GetDraftFolderData['query']) => ['folders', 'drafts', JSON.stringify(filters)],
  draftStats: (filters: GetDraftFolderData['query']) => [
    'folders',
    'drafts',
    'stats',
    JSON.stringify(filters),
  ],

  // mutations
  updateFolderPermissions: ['folders', 'updatePermissions'],
  moveFolder: ['folders', 'move'],
  moveItems: ['folders', 'items', 'move'],
  removeFolderItems: ['folders', 'items', 'remove'],
  createFolder: ['folders', 'create'],
  deleteFolder: ['folders', 'delete'],
  unlinkClone: (itemId: string) => ['folders', 'unlinkClone', itemId],
}

const COMMON = {
  // Just some time so it doesn't refetch immediately when used in different components
  staleTime: 1000 * 5, // 5 seconds
  refetchInterval: 1000 * 60 * 2, // every 2 minutes
}

export type FlatFolderMap = Map<string, FolderWithParent>
type ListFoldersWithFlatMap = Omit<ListFoldersOutput, 'folders'> & { folders: FlatFolderMap }

export const useFlatFolderMapWithStats = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.list,
    ...COMMON,
    queryFn: async (): Promise<ListFoldersWithFlatMap> => {
      const { data } = await api.listFolders({
        throwOnError: true,
      })

      const foldersMap: FlatFolderMap = new Map()

      getFlatFolderList(data.folders).forEach((folder) => {
        foldersMap.set(folder.id, folder)
      })

      return {
        ...data,
        folders: foldersMap,
      }
    },
  })
}

export const useFoldersFilterQuery = () => {
  const api = useApi()
  const filters = useMyFolders((state) => state.filters)

  const [debouncedFilters, setDebouncedFilters] = React.useState<Filters>(filters)

  const queryKey = React.useMemo(() => queryKeys.listSearch(filters), [debouncedFilters])

  const setFilters = React.useMemo(
    () =>
      _.debounce((newFilters: Filters) => {
        setDebouncedFilters(newFilters)
      }, 150),
    [],
  )

  React.useEffect(() => {
    setFilters(filters)
  }, [filters, setFilters])

  return useSuspenseQuery({
    queryKey,
    ...COMMON,
    queryFn: async () => {
      const isClone = filters.type === 'clone'
      const isInternal = filters.type === 'internal'

      const { data } = await api.searchItems({
        query: {
          search_term: filters.folderQuery,
          is_clone: isClone,
          is_internal: isInternal,
          domains: filters.domains,
          topics: filters.topics,
          profiles: filters.profiles,
          users: filters.users,
        },
        throwOnError: true,
      })

      return data
    },
  })
}

export const useFolderListBasic = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.listBasic,
    ...COMMON,
    queryFn: async () => {
      const { data } = await api.listFoldersBasic()
      return data
    },
  })
}

export const useFlatFolderList = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.flatList,
    ...COMMON,
    queryFn: async () => {
      const { data } = await api.listFoldersBasic()

      return getFlatFolderList(data || [])
    },
  })
}

export const useFolderDetailedQuery = (folderId?: string) => {
  const api = useApi()
  const { folderId: activeFolderId } = useFolderParams()
  const id = (folderId ?? activeFolderId)!

  if (!id) {
    console.warn('useFolderDetailedQuery called without folderId')
  }

  return useSuspenseQuery({
    queryKey: queryKeys.details(id),
    queryFn: async () => {
      const { data } = await api.getFolder({
        throwOnError: true,
        path: { folder_id: id },
      })

      return data
    },
    ...COMMON,
  })
}

const useDraftFilters = () => {
  const filters = useMyFolders((state) => state.filters)
  const [debouncedFilters, setDebouncedFilters] = React.useState<Filters>(filters)

  const setFilters = React.useMemo(
    () =>
      _.debounce((newFilters: Filters) => {
        setDebouncedFilters(newFilters)
      }, 150),
    [],
  )

  React.useEffect(() => {
    setFilters(filters)
  }, [filters, setFilters])

  return React.useMemo(
    () => ({
      search_term:
        debouncedFilters.folderQuery.length > 0 ? debouncedFilters.folderQuery : undefined,
      domains: debouncedFilters.domains,
      topics: debouncedFilters.topics,
      profiles: debouncedFilters.profiles,
      is_clone: debouncedFilters.type === FilterType.clone,
      is_internal: debouncedFilters.type === FilterType.internal,
    }),
    [debouncedFilters],
  )
}

const DRAFT_ITEMS_LIMIT = 50

export const useDraftItemsDetailedQuery = () => {
  const api = useApi()
  const limit = DRAFT_ITEMS_LIMIT

  const draftFilters = useDraftFilters()

  return useSuspenseInfiniteQuery({
    queryKey: queryKeys.drafts(draftFilters),
    queryFn: async ({ pageParam = 0 }) => {
      const { data } = await api.getDraftFolder({
        throwOnError: true,
        query: {
          limit,
          offset: pageParam as number,
          ...draftFilters,
        },
      })
      return data
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage.items.length === limit ? pages.length * limit : undefined,
    initialPageParam: 0,
    ...COMMON,
  })
}

export const useDraftStatsQuery = () => {
  const api = useApi()
  const draftFilters = useDraftFilters()

  return useQuery({
    queryKey: queryKeys.draftStats(draftFilters),
    ...COMMON,
    queryFn: async () => {
      const { data } = await api.getDraftStats({
        throwOnError: true,
        query: draftFilters,
      })

      return data
    },
  })
}

export const useItemDetailsQuery = (identifier: string, folderId?: string) => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.item(identifier),
    refetchInterval: 1000 * 60 * 1, // every 1 minute
    staleTime: 1000 * 2, // 2 seconds
    queryFn: async () => {
      const { data, error, response } = await api.getItemByIdentifier({
        path: { identifier },
        query: { folder_id: folderId },
      })

      if (error || !data) {
        throw new ItemFetchError(response.status, identifier)
      }

      return data
    },
  })
}

export const useMoveFolder = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.moveFolder,
    mutationFn: async (input: { folderId: string; folderName: string; folderTargetId: string }) => {
      const { data } = await api.updateFolder({
        path: { folder_id: input.folderId },
        body: {
          parentId: input.folderTargetId === 'root' ? null : input.folderTargetId,
          name: input.folderName,
        },
        throwOnError: true,
      })

      queryClient.refetchQueries({
        queryKey: ['folders', 'list'],
      })

      return data
    },
  })
}

export const useMoveItems = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.moveItems,
    mutationFn: async (body: MoveFolderItemsInput) => {
      const { data } = await api.moveFolderItems({
        body,
        throwOnError: true,
      })

      queryClient.refetchQueries({
        queryKey: ['folders', 'list'],
      })
      queryClient.refetchQueries({ queryKey: ['folders', 'details'] })

      return data
    },
  })
}

export const useRemoveItems = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.removeFolderItems,
    mutationFn: async (body: RemoveFolderItemsInput) => {
      const { data } = await api.removeFolderItems({
        body,
        throwOnError: true,
      })

      queryClient.refetchQueries({ queryKey: queryKeys.all })

      return data
    },
  })
}

export const useCreateFolder = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.createFolder,
    mutationFn: async (input: { name: string; parentId?: string }) => {
      const { data } = await api.createFolder({
        body: {
          name: input.name,
          parentId: input.parentId,
        },
        throwOnError: true,
      })

      queryClient.refetchQueries({
        queryKey: ['folders', 'list'],
      })

      return data
    },
  })
}

export const useDeleteFolder = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  const selectedFolderIds = useMyFolders((state) => state.selectedFolderIds)

  return useMutation({
    mutationKey: queryKeys.deleteFolder,
    mutationFn: async (input: { folderId: string }) => {
      const { data } = await api.deleteFolder({
        path: { folder_id: input.folderId },
        throwOnError: true,
      })

      await queryClient.refetchQueries({
        queryKey: ['folders', 'list'],
      })

      if (!selectedFolderIds.has(input.folderId)) return data

      // If the deleted folder was selected, we need to update the selectedFolderIds
      const foldersData = queryClient.getQueryData(queryKeys.list) as
        | ListFoldersWithFlatMap
        | undefined

      if (!foldersData?.folders) return data

      const allIds = [...foldersData.folders.keys()]

      const filteredSelected = new Set([...selectedFolderIds].filter((id) => allIds.includes(id)))

      if (filteredSelected.size !== selectedFolderIds.size) {
        useMyFolders.setState({
          selectedFolderIds: filteredSelected,
        })
      }

      return data
    },
  })
}

export const useRenameFolder = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.moveFolder,
    mutationFn: async (input: { folderId: string; folderName: string }) => {
      const { data } = await api.renameFolder({
        path: { folder_id: input.folderId },
        body: {
          name: input.folderName,
        },
        throwOnError: true,
      })

      queryClient.refetchQueries({
        queryKey: ['folders', 'list'],
      })

      return data
    },
  })
}

export const useGetFolderPermissions = (folderId: string) => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.permissions(folderId),
    refetchInterval: 1000 * 60 * 5, // every 5 minutes
    staleTime: 1000 * 60 * 1, // 1 minute
    queryFn: async () => {
      const { data } = await api.getFolderPermissions({
        throwOnError: true,
        path: { folder_id: folderId },
      })

      return data
    },
  })
}

export type UserAndFolders = {
  folders: Set<string>
} & FolderPermission['user']

export const useAggregatedFolderPermissionsMap = (): Record<string, UserAndFolders> => {
  const {
    data: { permissionsByFolder, folders },
  } = useFlatFolderMapWithStats()

  return React.useMemo(() => {
    const result: Record<string, UserAndFolders> = {}
    Object.entries(permissionsByFolder).forEach(([folderId, permissions]) => {
      permissions.forEach(({ user }) => {
        if (!result[user.email]) {
          result[user.email] = {
            ...user,
            folders: new Set(),
          }
        }
        result[user.email].folders.add(folderId)
      })
    })

    return result
  }, [permissionsByFolder, folders])
}

export const useAggregatedFolderPermissions = (): UserAndFolders[] => {
  const map = useAggregatedFolderPermissionsMap()
  return React.useMemo(() => Object.values(map), [map])
}

export const useFolderPermissionsCount = (folderId: string) => {
  const {
    data: { permissionsByFolder },
  } = useFlatFolderMapWithStats()

  return React.useMemo(() => {
    const permissions = permissionsByFolder[folderId]

    if (!permissions) {
      return 0
    }

    return permissions.length
  }, [permissionsByFolder[folderId]])
}

export const useUpdateFolderPermissions = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.updateFolderPermissions,
    mutationFn: async (input: {
      folderId: string
      permissions: UpdateFolderPermissionsInput[]
    }) => {
      await api.updateFolderPermissions({
        path: { folder_id: input.folderId },
        body: input.permissions,
      })

      // refetch the folder permissions and holds the promise so we block further updates
      await queryClient.invalidateQueries({
        queryKey: queryKeys.permissions(input.folderId),
      })
    },
  })
}

export const usePrefetchItem = (identifier?: string, folderId?: string) => {
  const api = useApi()
  const queryClient = useQueryClient()

  return () => {
    if (!identifier) return

    return queryClient.prefetchQuery({
      queryKey: queryKeys.item(identifier),
      staleTime: 1000 * 10, // 10 seconds
      queryFn: async () => {
        const result = await api.getItemByIdentifier({
          path: { identifier },
          query: { folder_id: folderId },
        })

        if (result.error) {
          if (result.response.status === 404 && folderId) {
            queryClient.invalidateQueries({ queryKey: queryKeys.details(folderId) })
          }
          throw result.error
        }

        return result.data
      },
    })
  }
}

export const usePreValidateExport = (queryKey: string, input: PreValidateExportInput) => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: queryKeys.preValidateExport(queryKey),
    retry: 0,
    refetchInterval: 1000 * 60 * 1, // every 1 minute
    queryFn: async () => {
      const { data } = await api.preValidateExport({
        body: input,
        throwOnError: true,
      })

      return data
    },
  })
}

export const useUnlinkClone = (itemId: string, itemIdentifier: string) => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: queryKeys.unlinkClone(itemId),
    mutationFn: async () => {
      await api.unlinkClone({ path: { item_id: itemId }, throwOnError: true })
      await queryClient.invalidateQueries({ queryKey: queryKeys.item(itemIdentifier) })
    },
    onError: () => {
      addSnack({
        message: i18n.t('errors.unlink_clone', { ns: 'folders' }),
        severity: 'error',
      })
    },
  })
}
